import React, { useState } from 'react';
import { PageShell, StatCard } from '../../components/Ui';
import { MdBuild, MdAdd, MdWarning, MdCheckCircle, MdTimer } from 'react-icons/md';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useAuth } from '../../auth/AuthContext';
import { normalizeRole } from '../../permissions';
import { escapeHtml } from '../../utils/escapeHtml';

const CATEGORIES = ['Electrical', 'Plumbing', 'Biomedical', 'IT/Network', 'HVAC/AC', 'Generator', 'Civil', 'Other'];
const PRIORITY_COLOR = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };
const STATUS_COLOR = { Open: '#ef4444', 'In Progress': '#f59e0b', Resolved: '#10b981' };

const today = () => new Date().toISOString().slice(0, 10);

function addDays(dateStr, days) {
  const d = new Date(dateStr || Date.now());
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function equipStatus(eq) {
  if (eq.status === 'Maintenance Due') return 'Maintenance Due';
  if (eq.nextDue && eq.nextDue <= today()) return 'Maintenance Due';
  return eq.status || 'Active';
}

function printWorkOrder(complaint, hospitalName) {
  const win = window.open('', '_blank', 'width=800,height=600');
  win.document.write(`<!DOCTYPE html><html><head><title>Work Order — ${escapeHtml(complaint.complaintId || complaint.id || '')}</title>
<style>body{font-family:Arial,sans-serif;margin:32px;color:#1e293b}h1{color:#dc2626}table{width:100%;border-collapse:collapse;margin:12px 0}th,td{border:1px solid #e2e8f0;padding:8px 12px;text-align:left}th{background:#f8fafc;font-weight:700;width:200px}@media print{button{display:none}}</style>
</head><body>
<h1>Maintenance Work Order</h1>
<p><strong>${escapeHtml(hospitalName || 'Hospital')}</strong></p>
<table>
<tr><th>Work Order #</th><td>${escapeHtml(String(complaint.complaintId || complaint.id || ''))}</td></tr>
<tr><th>Title</th><td>${escapeHtml(complaint.title || '')}</td></tr>
<tr><th>Category</th><td>${escapeHtml(complaint.category || '')}</td></tr>
<tr><th>Location</th><td>${escapeHtml(complaint.location || '')}</td></tr>
<tr><th>Priority</th><td>${escapeHtml(complaint.priority || '')}</td></tr>
<tr><th>Reported By</th><td>${escapeHtml(complaint.reportedBy || '')}</td></tr>
<tr><th>Assigned To</th><td>${escapeHtml(complaint.assignedTo || '—')}</td></tr>
<tr><th>Date Reported</th><td>${escapeHtml(complaint.date || '')}</td></tr>
<tr><th>Status</th><td>${escapeHtml(complaint.status || '')}</td></tr>
${complaint.closingNotes ? `<tr><th>Closing Notes</th><td>${escapeHtml(complaint.closingNotes)}</td></tr>` : ''}
${complaint.resolvedAt ? `<tr><th>Resolved At</th><td>${escapeHtml(complaint.resolvedAt)}</td></tr>` : ''}
</table>
<br><br>
<table style="width:auto">
<tr><td style="width:240px"><strong>Engineer Signature:</strong> ___________________</td><td style="width:240px"><strong>Manager Approval:</strong> ___________________</td></tr>
</table>
<br><button onclick="window.print()">Print</button>
</body></html>`);
  win.document.close();
}

export default function MaintenancePage() {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const isManager = role === 'manager';
  const staffPortal = !isManager;
  const hospitalName = user?.hospital || 'Hospital';

  const { data, loading, error, reload } = useLiveData(() => ipApi.getMaintenanceComplaints(100), []);
  const complaints = data?.complaints || [];

  const { data: equipData, loading: equipLoading, reload: reloadEquip } = useLiveData(
    () => (isManager ? ipApi.getMaintenanceEquipment(100) : Promise.resolve({ equipment: [] })),
    [isManager],
  );
  const equipment = equipData?.equipment || [];

  const [tab, setTab] = useState('complaints');
  const [showForm, setShowForm] = useState(staffPortal);
  const [showEquipForm, setShowEquipForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    title: '', category: 'Electrical', location: '', priority: 'Medium', reportedBy: user?.name || '', assignedTo: '',
  });
  const [equipForm, setEquipForm] = useState({
    name: '', type: 'General', dept: '', lastMaintenance: today(), nextDue: '', amcExpiry: '', status: 'Active', intervalDays: '',
  });
  const [resolvingId, setResolvingId] = useState(null);
  const [closingNotes, setClosingNotes] = useState('');

  const open = complaints.filter((c) => c.status === 'Open').length;
  const inProgress = complaints.filter((c) => c.status === 'In Progress').length;
  const resolvedToday = complaints.filter((c) => {
    if (c.status !== 'Resolved') return false;
    const resolvedDay = String(c.resolvedAt || '').slice(0, 10);
    return resolvedDay ? resolvedDay === today() : c.date === today();
  }).length;
  const activeComplaints = complaints.filter((c) => c.status !== 'Resolved').length;
  const dueForMaintenance = equipment.filter((e) => equipStatus(e) === 'Maintenance Due').length;

  const addComplaint = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await ipApi.createMaintenanceComplaint({
        ...form,
        reportedBy: form.reportedBy || user?.name || user?.email || 'Staff',
      });
      setShowForm(false);
      setForm({ title: '', category: 'Electrical', location: '', priority: 'Medium', reportedBy: user?.name || '', assignedTo: '' });
      setMsg('Issue reported — maintenance team will be notified.');
      reload();
    } catch (err) {
      setMsg(err.message || 'Could not submit');
    } finally {
      setSaving(false);
    }
  };

  const addEquipment = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await ipApi.createMaintenanceEquipment(equipForm);
      setShowEquipForm(false);
      setEquipForm({ name: '', type: 'General', dept: '', lastMaintenance: today(), nextDue: '', amcExpiry: '', status: 'Active', intervalDays: '' });
      setMsg('Equipment added to registry.');
      reloadEquip();
    } catch (err) {
      setMsg(err.message || 'Could not add equipment');
    } finally {
      setSaving(false);
    }
  };

  const markEquipmentMaintained = async (eq) => {
    const id = eq.equipmentId || eq.complaintId;
    const now = today();
    const intervalDays = Number(eq.intervalDays || eq.frequency || 0);
    const nextDueDate = intervalDays > 0 ? addDays(now, intervalDays) : (eq.nextDue || now);
    try {
      await ipApi.patchMaintenanceEquipment(id, {
        lastMaintenance: now,
        nextDue: nextDueDate,
        status: 'Active',
      });
      reloadEquip();
    } catch (err) {
      setMsg(err.message || 'Update failed');
    }
  };

  const updateStatus = async (id, status) => {
    if (status === 'Resolved') {
      setResolvingId(id);
      return;
    }
    try {
      await ipApi.patchMaintenanceComplaint(id, { status });
      reload();
    } catch (err) {
      setMsg(err.message || 'Update failed');
    }
  };

  const confirmResolve = async (id) => {
    try {
      await ipApi.patchMaintenanceComplaint(id, {
        status: 'Resolved',
        resolvedAt: new Date().toISOString(),
        closingNotes: closingNotes,
      });
      setResolvingId(null);
      setClosingNotes('');
      reload();
    } catch (err) {
      setMsg(err.message || 'Update failed');
    }
  };

  const title = staffPortal ? 'Report Facility Issue' : 'Facility & Equipment Maintenance';
  const subtitle = staffPortal
    ? 'Raise maintenance requests — your manager and engineering team will track them here.'
    : 'Complaint tracking · AMC monitoring · Preventive maintenance schedules';

  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      actions={(
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
          <MdAdd /> {showForm ? 'Close form' : 'Report Issue'}
        </button>
      )}
    >
      {error && (
        <div className="card mb" style={{ borderColor: '#fca5a5', background: '#fef2f2' }}>
          {error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
        </div>
      )}
      {msg && <p className="hint mb" style={{ color: msg.includes('notified') || msg.includes('added') ? '#16a34a' : '#dc2626' }}>{msg}</p>}

      <div className="stat-grid">
        <StatCard label="Open Complaints" value={open} accent="#ef4444" icon={MdWarning} />
        <StatCard label="In Progress" value={inProgress} accent="#f59e0b" icon={MdTimer} />
        <StatCard label="Resolved Today" value={resolvedToday} accent="#10b981" icon={MdCheckCircle} />
        {isManager && <StatCard label="Equipment Due" value={dueForMaintenance} accent="#8b5cf6" icon={MdBuild} />}
      </div>

      {showForm && (
        <form className="card mb form-grid" onSubmit={addComplaint}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Report Maintenance Issue</h3>
          <div className="field full">
            <label>Issue title *</label>
            <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. AC not working, Water leakage..." />
          </div>
          <div className="field">
            <label>Category</label>
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Location *</label>
            <input className="input" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ward B, OT-1, Lab, Reception..." />
          </div>
          <div className="field">
            <label>Priority</label>
            <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option>High</option><option>Medium</option><option>Low</option>
            </select>
          </div>
          {!staffPortal && (
            <>
              <div className="field"><label>Reported by</label><input className="input" value={form.reportedBy} onChange={(e) => setForm({ ...form, reportedBy: e.target.value })} /></div>
              <div className="field"><label>Assigned to</label><input className="input" value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} placeholder="Engineer / Team" /></div>
            </>
          )}
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Submitting…' : 'Submit'}</button>
          </div>
        </form>
      )}

      {isManager && (
        <div className="period-tabs card mb">
          {[
            { id: 'complaints', label: `🔧 Complaints (${activeComplaints})` },
            { id: 'equipment', label: `⚙ Equipment (${equipment.length})` },
            { id: 'amc', label: '📄 AMC Tracker' },
          ].map(({ id, label }) => (
            <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      )}

      {(tab === 'complaints' || staffPortal) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading && <p className="hint">Loading complaints…</p>}
          {complaints.map((c) => (
            <div key={c.complaintId} className="maint-card" style={{ borderLeft: `4px solid ${STATUS_COLOR[c.status] || '#94a3b8'}` }}>
              <div className="maint-card-head">
                <div>
                  <div className="maint-title">{c.title}</div>
                  <div className="maint-meta">
                    <span className="maint-priority-chip" style={{ background: `${PRIORITY_COLOR[c.priority]}20`, color: PRIORITY_COLOR[c.priority], border: `1px solid ${PRIORITY_COLOR[c.priority]}40` }}>{c.priority}</span>
                    <span>📍 {c.location}</span>
                    <span>🗂 {c.category}</span>
                    <span>📅 {c.date}</span>
                    {c.reportedBy && <span>🙋 {c.reportedBy}</span>}
                    {c.assignedTo && <span>👷 {c.assignedTo}</span>}
                  </div>
                  {c.closingNotes && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>📝 {c.closingNotes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <span className="ot-status-chip" style={{ background: `${STATUS_COLOR[c.status]}20`, color: STATUS_COLOR[c.status], border: `1px solid ${STATUS_COLOR[c.status]}40` }}>{c.status}</span>
                  <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10 }} onClick={() => printWorkOrder(c, hospitalName)}>🖨 WO</button>
                </div>
              </div>
              {isManager && (
                <div className="ot-card-actions">
                  {['Open', 'In Progress', 'Resolved'].map((st) => (
                    <button key={st} type="button" className="ins-status-btn" style={{ background: c.status === st ? STATUS_COLOR[st] : undefined, color: c.status === st ? '#fff' : undefined }} onClick={() => updateStatus(c.complaintId, st)}>{st}</button>
                  ))}
                </div>
              )}
              {resolvingId === c.complaintId && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
                  <div className="field mb">
                    <label style={{ fontSize: 12 }}>Closing Notes (optional)</label>
                    <textarea className="input" rows={2} value={closingNotes} onChange={e => setClosingNotes(e.target.value)} placeholder="What was done to resolve the issue..." />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => confirmResolve(c.complaintId)}>✓ Confirm Resolved</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setResolvingId(null); setClosingNotes(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {!loading && complaints.length === 0 && <p className="hint table-empty">No maintenance complaints yet. Tap Report Issue to raise one.</p>}
        </div>
      )}

      {isManager && tab === 'equipment' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>Equipment Registry</h3>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowEquipForm((s) => !s)}>
              {showEquipForm ? 'Close' : '+ Add equipment'}
            </button>
          </div>
          {showEquipForm && (
            <form className="form-grid mb" onSubmit={addEquipment}>
              <div className="field"><label>Name *</label><input className="input" required value={equipForm.name} onChange={(e) => setEquipForm({ ...equipForm, name: e.target.value })} /></div>
              <div className="field"><label>Type</label><input className="input" value={equipForm.type} onChange={(e) => setEquipForm({ ...equipForm, type: e.target.value })} /></div>
              <div className="field"><label>Department</label><input className="input" value={equipForm.dept} onChange={(e) => setEquipForm({ ...equipForm, dept: e.target.value })} /></div>
              <div className="field"><label>Last maintenance</label><input type="date" className="input" value={equipForm.lastMaintenance} onChange={(e) => setEquipForm({ ...equipForm, lastMaintenance: e.target.value })} /></div>
              <div className="field"><label>Next due</label><input type="date" className="input" value={equipForm.nextDue} onChange={(e) => setEquipForm({ ...equipForm, nextDue: e.target.value })} /></div>
              <div className="field"><label>Interval (days) — auto-sets nextDue on done</label><input type="number" className="input" value={equipForm.intervalDays} onChange={(e) => setEquipForm({ ...equipForm, intervalDays: e.target.value })} placeholder="e.g. 90" /></div>
              <div className="field"><label>AMC expiry</label><input type="date" className="input" value={equipForm.amcExpiry} onChange={(e) => setEquipForm({ ...equipForm, amcExpiry: e.target.value })} /></div>
              <div className="form-actions full">
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save equipment'}</button>
              </div>
            </form>
          )}
          {equipLoading && <p className="hint">Loading equipment…</p>}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Equipment', 'Dept', 'Last Maintenance', 'Next Due', 'Interval', 'Status', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {equipment.map((eq) => {
                  const status = equipStatus(eq);
                  return (
                    <tr key={eq.equipmentId || eq.complaintId}>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9', fontWeight: 600 }}>{eq.name}</td>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9', color: '#64748b' }}>{eq.dept}</td>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9' }}>{eq.lastMaintenance}</td>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9', color: status === 'Maintenance Due' ? '#ef4444' : '#0f172a', fontWeight: status === 'Maintenance Due' ? 700 : 400 }}>{eq.nextDue || '—'}</td>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9', color: '#64748b' }}>{eq.intervalDays ? `${eq.intervalDays}d` : '—'}</td>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: status === 'Active' ? '#f0fdf4' : '#fef2f2', color: status === 'Active' ? '#16a34a' : '#dc2626' }}>{status}</span>
                      </td>
                      <td style={{ padding: '10px 12px', border: '1px solid #f1f5f9' }}>
                        {status === 'Maintenance Due' && (
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => markEquipmentMaintained(eq)}>Mark done</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!equipLoading && equipment.length === 0 && <p className="hint table-empty">No equipment registered yet.</p>}
        </div>
      )}

      {isManager && tab === 'amc' && (
        <div className="card">
          <h3>AMC (Annual Maintenance Contract) Tracker</h3>
          <p className="hint mb">Track equipment AMC expiry. Highlighted in red = expiring within 90 days.</p>
          {equipLoading && <p className="hint">Loading…</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {equipment.filter((eq) => eq.amcExpiry).map((eq) => {
              const days = Math.floor((new Date(eq.amcExpiry) - Date.now()) / 86400000);
              return (
                <div key={eq.equipmentId || eq.complaintId} className="maint-amc-card" style={{ borderLeft: `4px solid ${days < 0 ? '#ef4444' : days < 90 ? '#f59e0b' : '#10b981'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{eq.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{eq.dept} · {eq.type}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: days < 0 ? '#ef4444' : days < 90 ? '#ca8a04' : '#16a34a' }}>
                        {days < 0 ? `EXPIRED ${Math.abs(days)} days ago` : days < 90 ? `Expires in ${days} days` : `Valid · ${days} days left`}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>AMC Expiry: {eq.amcExpiry}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!equipLoading && equipment.filter((eq) => eq.amcExpiry).length === 0 && (
            <p className="hint table-empty">Add equipment with AMC expiry dates to track contracts here.</p>
          )}
        </div>
      )}
    </PageShell>
  );
}
